import { periodContains, type DashboardPeriod } from "@/lib/dashboard-period";

export interface HourlyRateSalesUnitInput {
  id: string;
  name: string;
}

export interface HourlyRateProjectInput {
  id: string;
  name: string;
  projectCode: string | null;
  completedAt: string | null;
  bfActual: number | null;
}

export interface HourlyRateOwnerInput {
  projectId: string;
  salesUnitId: string | null;
  ownershipPct: number | null;
}

export interface HourlyRateTimeEntryInput {
  projectId: string | null;
  durationSeconds: number | null;
}

export interface SalesUnitHourlyRateProject {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  ownershipPct: number;
  geekAmount: number;
  loggedHours: number;
  hourlyRate: number | null;
  included: boolean;
}

export interface SalesUnitHourlyRateRow {
  unitId: string;
  unitName: string;
  geekAmount: number;
  loggedHours: number;
  hourlyRate: number | null;
  includedProjectCount: number;
  excludedProjectCount: number;
  projects: SalesUnitHourlyRateProject[];
}

/**
 * Reell timepris per salgsenhet for fullførte prosjekter i valgt periode.
 *
 * - GEEK = projects.bf_actual (samme reelle GEEK som på prosjektkortet).
 * - Timer = alle ikke-slettede timeføringer, lifetime på prosjektet.
 * - Ved delt eierskap vektes både GEEK og timer med salgsenhetens samlede
 *   ownership_pct. Dermed summerer drilldown nøyaktig til enhetsoversikten.
 * - Prosjekter uten positive timer vises som datamangel, men holdes helt ute
 *   av både teller og nevner.
 */
export function calcSalesUnitHourlyRates(input: {
  period: DashboardPeriod;
  salesUnits: HourlyRateSalesUnitInput[];
  projects: HourlyRateProjectInput[];
  owners: HourlyRateOwnerInput[];
  timeEntries: HourlyRateTimeEntryInput[];
}): SalesUnitHourlyRateRow[] {
  const completedProjects = new Map(
    input.projects
      .filter((project) => project.completedAt && periodContains(input.period, project.completedAt))
      .map((project) => [project.id, project]),
  );

  const hoursByProject = new Map<string, number>();
  for (const entry of input.timeEntries) {
    if (!entry.projectId) continue;
    hoursByProject.set(
      entry.projectId,
      (hoursByProject.get(entry.projectId) ?? 0) + Number(entry.durationSeconds ?? 0) / 3600,
    );
  }

  // NewProjectWizard lager én owner-rad per medlem. Enhetens andel er derfor
  // summen av medlemsradene for samme prosjekt og salgsenhet.
  const shareByUnitProject = new Map<string, number>();
  for (const owner of input.owners) {
    if (!owner.salesUnitId || !completedProjects.has(owner.projectId)) continue;
    const key = `${owner.salesUnitId}:${owner.projectId}`;
    shareByUnitProject.set(key, (shareByUnitProject.get(key) ?? 0) + Number(owner.ownershipPct ?? 0));
  }

  const rows = input.salesUnits.map((unit): SalesUnitHourlyRateRow => {
    const projects: SalesUnitHourlyRateProject[] = [];

    for (const project of completedProjects.values()) {
      const ownershipPct = shareByUnitProject.get(`${unit.id}:${project.id}`) ?? 0;
      if (ownershipPct <= 0) continue;

      const share = ownershipPct / 100;
      const rawHours = hoursByProject.get(project.id) ?? 0;
      const geekAmount = Number(project.bfActual ?? 0) * share;
      const loggedHours = rawHours * share;
      const included = loggedHours > 0;

      projects.push({
        projectId: project.id,
        projectName: project.name,
        projectCode: project.projectCode,
        ownershipPct,
        geekAmount,
        loggedHours,
        hourlyRate: included ? geekAmount / loggedHours : null,
        included,
      });
    }

    projects.sort((a, b) => {
      if (a.hourlyRate == null && b.hourlyRate == null) return a.projectName.localeCompare(b.projectName, "nb");
      if (a.hourlyRate == null) return 1;
      if (b.hourlyRate == null) return -1;
      return b.hourlyRate - a.hourlyRate;
    });

    const included = projects.filter((project) => project.included);
    const geekAmount = included.reduce((sum, project) => sum + project.geekAmount, 0);
    const loggedHours = included.reduce((sum, project) => sum + project.loggedHours, 0);

    return {
      unitId: unit.id,
      unitName: unit.name,
      geekAmount,
      loggedHours,
      hourlyRate: loggedHours > 0 ? geekAmount / loggedHours : null,
      includedProjectCount: included.length,
      excludedProjectCount: projects.length - included.length,
      projects,
    };
  });

  rows.sort((a, b) => {
    if (a.hourlyRate == null && b.hourlyRate == null) return a.unitName.localeCompare(b.unitName, "nb");
    if (a.hourlyRate == null) return 1;
    if (b.hourlyRate == null) return -1;
    return b.hourlyRate - a.hourlyRate;
  });
  return rows;
}
